import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin } from 'lucide-react';

import logoLight from '@/assets/images/logo/yidm_bglg.png';
import logoDark from '@/assets/images/logo/yidm_logo.png';

export const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { path: '/', label: t('header.home') },
    { path: '/capabilities', label: t('header.capabilities') },
    { path: '/machinery', label: t('header.machinery') },
    { path: '/about', label: t('header.about') }
  ];

  // minimal state to re-render when theme changes
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;

    // observe class changes on <html>
    const target = document.documentElement;
    const mo = new MutationObserver(() => {
      setIsDark(target.classList.contains('dark'));
    });

    mo.observe(target, { attributes: true, attributeFilter: ['class'] });

    // also listen for storage changes (theme updated in another tab)
    const onStorage = (e) => {
      if (e.key === 'theme') {
        setIsDark(e.newValue === 'dark');
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      mo.disconnect();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const logoSrc = isDark ? logoDark : logoLight;

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {/* Company Info */}
          <div>
            <div className="flex items-start gap-4">
              <div
                className="flex items-center justify-center rounded-lg flex-shrink-0"
                style={{
                  width: 56,
                  height: 56,
                  background: '#fff',
                }}
                aria-hidden
              >
                <img
                  src={logoSrc}
                  alt=""
                  className="h-8 w-auto object-contain"
                  draggable="false"

                />
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-lg font-semibold leading-tight">
                  {t("footer.title")}
                </span>
                <p className="mt-1 text-sm text-secondary-foreground/80 max-w-xs">
                  {t('footer.tagline') || 'Precision tooling & diecasting'}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <nav aria-label={t('footer.quick_links')} className="order-last md:order-none">
            <h3 className="text-sm font-semibold mb-4">{t('footer.quick_links')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-secondary-foreground/80 hover:text-secondary-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold mb-4">{t('footer.contact_info')}</h3>
            <address className="not-italic space-y-3">
              <div className="flex items-start gap-3 text-sm text-secondary-foreground/80">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('footer.address_text')}</span>
              </div>

              <div className="flex items-center gap-3 text-sm text-secondary-foreground/80">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <a href="tel:+919116967045" className="hover:underline">
                  +91 9116967045
                </a>
              </div>

              <div className="flex items-start gap-3 text-sm text-secondary-foreground/80">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col">
                  <a
                    href="mailto:dhiraj@yazaki-diecastmold.co.in"
                    className="hover:underline text-secondary-foreground/70"
                  >
                    dhiraj@yazaki-diecastmold.co.in
                  </a>
                  <a
                    href="mailto:sales@yazaki-diecastmold.co.in"
                    className="hover:underline"
                  >
                    sales@yazaki-diecastmold.co.in
                  </a>
                </div>
              </div>

            </address>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-secondary-foreground/10 text-center">
          <p className="text-sm text-secondary-foreground/60">
            © {currentYear} Yazaki India Diecast Mold Pvt. Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
